<script lang="ts" setup>
import { ref } from 'vue';
import Chip from 'primevue/chip';
import { EMIT, PERMISSIONS } from '@/enum';
import { can } from '@/composables';
import BuildingsListActions from './BuildingsListActions.vue';
import AdminManageBuildingModal from './AdminManageBuildingModal.vue';
import ClinicianCreateBuildingModal from './ClinicianCreateBuildingModal.vue';
import { usePeopleTypesStore } from '@/store';

type CreateBuildingData = {
  name: string;
  attachClinicians?: number[];
};

type PeopleFilterInterface = {
  id: number;
  display_name: string;
  person_type_id: number;
};

const props = defineProps<{
  organizationId: number;
  isAdminUser: boolean;
  totalRecords: number;
  loading: boolean;
  buildings: [];
  buildingsOrganization: [];
}>();

const emit = defineEmits([EMIT.REMOVE, EMIT.SAVE, EMIT.EDIT, EMIT.SYNC]);
const showAddBuilding = ref(false);
const showEditBuilding = ref(false);
const building_id = ref(0);
const initialName = ref();
const initialSelectedClinicians = ref<PeopleFilterInterface[]>([]);
const showClinicianModal = ref(false);
const peopleTypesStore = usePeopleTypesStore();

const deleteBuilding = async (building) => {
  emit(EMIT.REMOVE, building);
};

const openAddBuildingModal = () => {
  showAddBuilding.value = true;
};

const closeAddBuilding = () => {
  showAddBuilding.value = false;
  showEditBuilding.value = false;
};

const createBuilding = (data: CreateBuildingData) => {
  emit(EMIT.SAVE, data);
  showAddBuilding.value = false;
  showClinicianModal.value = false;
};

const openClinicianModal = () => {
  showClinicianModal.value = true;
};

const closeClinicianModal = () => {
  showClinicianModal.value = false;
};

const openModal = () => {
  if (props.isAdminUser) {
    openAddBuildingModal();
  } else {
    emit(EMIT.SYNC);
    openClinicianModal();
  }
};

const onEditBuilding = (building: { id: number; name: string; people?: [] }) => {
  building_id.value = building.id;
  initialName.value = building.name;
  initialSelectedClinicians.value = filteredPeople(building.people) || [];
  showEditBuilding.value = true;
};

const editBuilding = async (data) => {
  data.building_id = building_id.value;
  emit(EMIT.EDIT, data);
  showAddBuilding.value = false;
  showEditBuilding.value = false;
};

const filteredPeople = (people: PeopleFilterInterface[]) => {
  return people.filter((person) => person.people_type.acronym == peopleTypesStore.PEOPLE_TYPES.CLINICIAN);
};
</script>

<template>
  <Card>
    <template #header>
      <h2 class="text-xl font-semibold">Buildings</h2>
      <Chip>{{ props.totalRecords }}</Chip>
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.BUILDING_ASSIGN])"
          type="text"
          size="small"
          text="Add Building"
          icon-left="add"
          @click="openModal"
        />
      </div>
    </template>

    <template #content>
      <DataTable
        :value="props.buildings"
        :loading="props.loading"
        data-Key="organization_id"
        responsive-layout="scroll"
        :sort-field="'name'"
        :sort-order="1"
      >
        <template v-if="props.totalRecords > 0">
          <template v-if="props.isAdminUser">
            <Column field="name" header="Buildings" sortable />
            <Column field="service_providers" header="Service Providers">
              <template #body="{ data }">
                <ul style="list-style: none; padding: 0; margin: 0">
                  <li v-if="!data.people || filteredPeople(data.people).length === 0">
                    Service Providers not assigned
                  </li>
                  <li v-for="person in filteredPeople(data.people)" :key="person.id">
                    {{ person.display_name }}
                  </li>
                </ul>
              </template>
            </Column>
          </template>
          <template v-else>
            <Column field="building_name" header="Buildings" sortable />
          </template>
          <Column v-if="can([PERMISSIONS.BUILDING_ASSIGN])" field="actions" header="Actions">
            <template #body="{ data }">
              <BuildingsListActions @save="() => deleteBuilding(data)" @edit="() => onEditBuilding(data)" />
            </template>
          </Column>
        </template>
        <template #empty>Buildings not assigned.</template>
      </DataTable>
    </template>
  </Card>
  <div v-if="props.isAdminUser">
    <AdminManageBuildingModal
      v-if="showAddBuilding"
      :organization-id="organizationId"
      mode="add"
      @cancel="closeAddBuilding"
      @save="createBuilding"
    />

    <AdminManageBuildingModal
      v-if="showEditBuilding"
      :organization-id="organizationId"
      mode="edit"
      :initial-name="initialName"
      :initial-selected-clinicians="initialSelectedClinicians"
      @cancel="closeAddBuilding"
      @edit="editBuilding"
    />
  </div>
  <div v-else>
    <ClinicianCreateBuildingModal
      v-if="showClinicianModal"
      :buildings="props.buildings"
      :buildings-organization="props.buildingsOrganization"
      @cancel="closeClinicianModal"
      @save="createBuilding"
    />
  </div>
</template>

<style lang="scss" scoped>
.p-card {
  min-width: calc(100% - 1.5rem);
  .p-card-body {
    padding-bottom: 0px;
  }
}
</style>
